import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Single switch handler for header selector, dashboard cards, and empty-state picks.
 * Writes selectedCustomerId only — never touches token / logout.
 * Family cart (PA2-03): switching child does not clear other children’s lines.
 */
export default function useSwitchChild() {
	const dispatch = useDispatch();
	const childrenRaw = useSelector((state) => state.auth.children);
	const children = useMemo(() => childrenRaw || [], [childrenRaw]);
	const selectedCustomerId = useSelector((state) => state.auth.selectedCustomerId);
	const switchingLocked = useSelector((state) => state.auth.switchingLocked);
	const pendingSwitchCustomerId = useSelector((state) => state.auth.pendingSwitchCustomerId);

	const applySwitch = useCallback(
		(id) => {
			dispatch({ type: 'auth/setPendingSwitchCustomerId', payload: null });
			dispatch({ type: 'cart/clearDiscount' });
			dispatch({ type: 'auth/setSelectedCustomerId', payload: id });
			return { ok: true, reason: 'switched' };
		},
		[dispatch]
	);

	const switchChild = useCallback(
		(customerId) => {
			if (switchingLocked) {
				return { ok: false, reason: 'locked' };
			}

			if (customerId == null || customerId === '') {
				return { ok: false, reason: 'missing' };
			}

			const id = Number(customerId);
			const allowed = children.some((c) => Number(c.id) === id);
			if (!allowed) {
				return { ok: false, reason: 'unauthorized' };
			}

			if (Number(selectedCustomerId) === id) {
				dispatch({ type: 'auth/setPendingSwitchCustomerId', payload: null });
				return { ok: true, reason: 'unchanged' };
			}

			return applySwitch(id);
		},
		[applySwitch, children, dispatch, selectedCustomerId, switchingLocked]
	);

	/** Kept for SwitchChildConfirm; clears cart then switches (legacy / explicit clear). */
	const confirmClearAndSwitch = useCallback(() => {
		if (pendingSwitchCustomerId == null) {
			return { ok: false, reason: 'missing' };
		}
		if (switchingLocked) {
			return { ok: false, reason: 'locked' };
		}
		const id = Number(pendingSwitchCustomerId);
		dispatch({ type: 'cart/resetItems' });
		return applySwitch(id);
	}, [applySwitch, dispatch, pendingSwitchCustomerId, switchingLocked]);

	const cancelPendingSwitch = useCallback(() => {
		dispatch({ type: 'auth/setPendingSwitchCustomerId', payload: null });
		return { ok: true, reason: 'stayed' };
	}, [dispatch]);

	return {
		switchChild,
		confirmClearAndSwitch,
		cancelPendingSwitch,
		switchingLocked,
		selectedCustomerId,
		pendingSwitchCustomerId,
		children,
	};
}
